const DB_NAME = "cochegest-offline";
const MUTATION_STORE = "mutations";
const KEY_STORE = "keys";
const DRAFT_STORE = "vehicle_intake_drafts";
// v3 invalida las claves antiguas, que solo incluían al usuario y podían
// mezclar operaciones si esa persona accedía a más de un tenant.
const DB_VERSION = 4;

let queueOwnerId: string | null = null;

export interface OfflineQueueScope {
  userId: string;
  tenantId: string;
}

export interface QueuedMutation {
  id: string;
  path: string;
  method: "POST" | "PATCH" | "DELETE";
  body: unknown;
  idempotencyKey: string;
  createdAt: string;
  attempts: number;
}

interface StoredMutation extends Omit<QueuedMutation, "body"> {
  ownerId: string;
  encryptedBody: string;
  iv: string;
}

interface StoredKey {
  id: string;
  key: CryptoKey;
}

const offlinePatterns: ReadonlyArray<{ method: QueuedMutation["method"]; pattern: RegExp }> = [
  { method: "POST", pattern: /^\/vehicles$/ },
  { method: "PATCH", pattern: /^\/vehicles\/[^/]+$/ },
  // Task 10: /expenses se retiró en favor de /costs (append-only, con reversión).
  { method: "POST", pattern: /^\/vehicles\/[^/]+\/(transitions|costs|movements|warranties)$/ },
  { method: "PATCH", pattern: /^\/vehicles\/[^/]+\/import-steps$/ },
  { method: "POST", pattern: /^\/vehicles\/[^/]+\/workshop-orders$/ },
  { method: "PATCH", pattern: /^\/vehicles\/[^/]+\/workshop-orders\/[^/]+$/ },
  { method: "POST", pattern: /^\/leads$/ },
  { method: "PATCH", pattern: /^\/leads\/[^/]+\/status$/ },
  { method: "POST", pattern: /^\/appraisals$/ },
  { method: "PATCH", pattern: /^\/appraisals\/[^/]+\/status$/ },
  { method: "POST", pattern: /^\/sites$/ },
  { method: "POST", pattern: /^\/documents\/[^/]+\/ocr\/apply$/ },
];

export function canQueueOfflineMutation(path: string, method: QueuedMutation["method"]): boolean {
  return offlinePatterns.some((candidate) => candidate.method === method && candidate.pattern.test(path));
}

export function offlineQueueOwner(scope: OfflineQueueScope): string {
  return [scope.userId, scope.tenantId].map(encodeURIComponent).join(":");
}

export function setOfflineQueueScope(scope: OfflineQueueScope | null): void {
  queueOwnerId = scope ? offlineQueueOwner(scope) : null;
}

function openDb(): Promise<IDBDatabase> {
  return new Promise((resolve, reject) => {
    const request = indexedDB.open(DB_NAME, DB_VERSION);
    request.onupgradeneeded = (event) => {
      const database = request.result;
      const oldVersion = (event as IDBVersionChangeEvent).oldVersion;
      let mutationStore: IDBObjectStore;
      if (!database.objectStoreNames.contains(MUTATION_STORE)) {
        mutationStore = database.createObjectStore(MUTATION_STORE, { keyPath: "id" });
      } else {
        mutationStore = request.transaction!.objectStore(MUTATION_STORE);
        if (oldVersion < 3) mutationStore.clear();
      }
      if (!mutationStore.indexNames.contains("owner_created")) {
        mutationStore.createIndex("owner_created", ["ownerId", "createdAt"]);
      }
      if (!database.objectStoreNames.contains(KEY_STORE)) {
        database.createObjectStore(KEY_STORE, { keyPath: "id" });
      }
      if (!database.objectStoreNames.contains(DRAFT_STORE)) {
        database.createObjectStore(DRAFT_STORE, { keyPath: "ownerId" });
      }
    };
    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error);
  });
}

function bytesToBase64(bytes: Uint8Array): string {
  let binary = "";
  for (const byte of bytes) binary += String.fromCharCode(byte);
  return btoa(binary);
}

function base64ToBytes(value: string): Uint8Array<ArrayBuffer> {
  const binary = atob(value);
  const bytes = new Uint8Array(new ArrayBuffer(binary.length));
  for (let index = 0; index < binary.length; index += 1) bytes[index] = binary.charCodeAt(index);
  return bytes;
}

async function ownerKey(database: IDBDatabase, ownerId: string): Promise<CryptoKey> {
  const existing = await new Promise<StoredKey | undefined>((resolve, reject) => {
    const request = database.transaction(KEY_STORE, "readonly").objectStore(KEY_STORE).get(ownerId);
    request.onsuccess = () => resolve(request.result as StoredKey | undefined);
    request.onerror = () => reject(request.error);
  });
  if (existing?.key) return existing.key;
  const key = await crypto.subtle.generateKey({ name: "AES-GCM", length: 256 }, false, ["encrypt", "decrypt"]);
  return new Promise<CryptoKey>((resolve, reject) => {
    const transaction = database.transaction(KEY_STORE, "readwrite");
    const store = transaction.objectStore(KEY_STORE);
    const check = store.get(ownerId);
    let selected = key;
    check.onsuccess = () => {
      // La cola y el borrador pueden inicializarse a la vez. Nunca reemplazar
      // una clave con la que la otra operación acaba de cifrar sus datos.
      const record = check.result as StoredKey | undefined;
      if (record?.key) selected = record.key;
      else store.put({ id: ownerId, key } satisfies StoredKey);
    };
    transaction.oncomplete = () => resolve(selected);
    transaction.onerror = () => reject(transaction.error);
    transaction.onabort = () => reject(transaction.error);
  });
}

async function encryptedBody(database: IDBDatabase, ownerId: string, body: unknown): Promise<{ encryptedBody: string; iv: string }> {
  const iv = crypto.getRandomValues(new Uint8Array(12));
  const plaintext = new TextEncoder().encode(JSON.stringify(body ?? null));
  const encrypted = await crypto.subtle.encrypt(
    { name: "AES-GCM", iv, additionalData: new TextEncoder().encode(ownerId) },
    await ownerKey(database, ownerId),
    plaintext,
  );
  return { encryptedBody: bytesToBase64(new Uint8Array(encrypted)), iv: bytesToBase64(iv) };
}

async function decryptedBody(database: IDBDatabase, record: Pick<StoredMutation, "ownerId" | "encryptedBody" | "iv">): Promise<unknown> {
  const decrypted = await crypto.subtle.decrypt(
    { name: "AES-GCM", iv: base64ToBytes(record.iv), additionalData: new TextEncoder().encode(record.ownerId) },
    await ownerKey(database, record.ownerId),
    base64ToBytes(record.encryptedBody),
  );
  return JSON.parse(new TextDecoder().decode(decrypted)) as unknown;
}

function requiredOwner(): string {
  if (!queueOwnerId) throw new Error("La cola offline necesita una sesión activa.");
  return queueOwnerId;
}

// El alta incompleta no entra en la cola de mutaciones: conserva su propia clave
// de reintento para no duplicar un POST cuyo resultado se haya perdido.
export async function readVehicleIntakeDraft(scope: OfflineQueueScope): Promise<unknown | null> {
  const ownerId = offlineQueueOwner(scope);
  const database = await openDb();
  try {
    const record = await new Promise<Pick<StoredMutation, "ownerId" | "encryptedBody" | "iv"> | undefined>((resolve, reject) => {
      const request = database.transaction(DRAFT_STORE, "readonly").objectStore(DRAFT_STORE).get(ownerId);
      request.onsuccess = () => resolve(request.result);
      request.onerror = () => reject(request.error);
    });
    return record ? await decryptedBody(database, record) : null;
  } finally { database.close(); }
}

export async function writeVehicleIntakeDraft(scope: OfflineQueueScope, draft: unknown | null): Promise<void> {
  const ownerId = offlineQueueOwner(scope);
  const database = await openDb();
  try {
    const encrypted = draft === null ? null : await encryptedBody(database, ownerId, draft);
    await new Promise<void>((resolve, reject) => {
      const transaction = database.transaction(DRAFT_STORE, "readwrite");
      const store = transaction.objectStore(DRAFT_STORE);
      if (encrypted) store.put({ ownerId, ...encrypted });
      else store.delete(ownerId);
      transaction.oncomplete = () => resolve();
      transaction.onerror = () => reject(transaction.error);
      transaction.onabort = () => reject(transaction.error);
    });
  } finally { database.close(); }
}

export async function enqueueMutation(
  mutation: Omit<QueuedMutation, "id" | "createdAt" | "attempts">,
): Promise<QueuedMutation> {
  const ownerId = requiredOwner();
  if (!canQueueOfflineMutation(mutation.path, mutation.method)) {
    throw new Error("Esta operación necesita conexión y no se guardará en el dispositivo.");
  }
  const record: QueuedMutation = {
    ...mutation,
    id: crypto.randomUUID(),
    createdAt: new Date().toISOString(),
    attempts: 0,
  };
  const database = await openDb();
  const encrypted = await encryptedBody(database, ownerId, mutation.body);
  await new Promise<void>((resolve, reject) => {
    const transaction = database.transaction(MUTATION_STORE, "readwrite");
    const { body: _body, ...metadata } = record;
    const stored: StoredMutation = { ...metadata, ownerId, ...encrypted };
    transaction.objectStore(MUTATION_STORE).put(stored);
    transaction.oncomplete = () => resolve();
    transaction.onerror = () => reject(transaction.error);
  });
  database.close();
  return record;
}

export async function getQueuedMutations(): Promise<QueuedMutation[]> {
  const ownerId = requiredOwner();
  const database = await openDb();
  const records = await new Promise<StoredMutation[]>((resolve, reject) => {
    const request = database.transaction(MUTATION_STORE, "readonly").objectStore(MUTATION_STORE).getAll();
    request.onsuccess = () => resolve((request.result as StoredMutation[]).filter((record) => record.ownerId === ownerId));
    request.onerror = () => reject(request.error);
  });
  const decrypted = await Promise.all(records.map(async (record) => ({
    id: record.id,
    path: record.path,
    method: record.method,
    body: await decryptedBody(database, record),
    idempotencyKey: record.idempotencyKey,
    createdAt: record.createdAt,
    attempts: record.attempts,
  })));
  database.close();
  return decrypted.sort((left, right) => left.createdAt.localeCompare(right.createdAt));
}

export async function recordMutationAttempt(id: string): Promise<void> {
  const ownerId = requiredOwner();
  const database = await openDb();
  await new Promise<void>((resolve, reject) => {
    const transaction = database.transaction(MUTATION_STORE, "readwrite");
    const store = transaction.objectStore(MUTATION_STORE);
    const request = store.get(id);
    request.onsuccess = () => {
      const record = request.result as StoredMutation | undefined;
      if (record?.ownerId === ownerId) store.put({ ...record, attempts: record.attempts + 1 });
    };
    transaction.oncomplete = () => resolve();
    transaction.onerror = () => reject(transaction.error);
  });
  database.close();
}

export async function removeQueuedMutation(id: string): Promise<void> {
  const ownerId = requiredOwner();
  const database = await openDb();
  await new Promise<void>((resolve, reject) => {
    const transaction = database.transaction(MUTATION_STORE, "readwrite");
    const store = transaction.objectStore(MUTATION_STORE);
    const request = store.get(id);
    request.onsuccess = () => {
      const record = request.result as StoredMutation | undefined;
      if (record?.ownerId === ownerId) store.delete(id);
    };
    transaction.oncomplete = () => resolve();
    transaction.onerror = () => reject(transaction.error);
  });
  database.close();
}

export async function countQueuedMutations(): Promise<number> {
  if (!queueOwnerId) return 0;
  const database = await openDb();
  const ownerId = queueOwnerId;
  const count = await new Promise<number>((resolve, reject) => {
    const request = database.transaction(MUTATION_STORE, "readonly").objectStore(MUTATION_STORE).getAll();
    request.onsuccess = () => resolve((request.result as StoredMutation[]).filter((record) => record.ownerId === ownerId).length);
    request.onerror = () => reject(request.error);
  });
  database.close();
  return count;
}
